import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { adminApi } from "../../admin/api";
import { ErrorText } from "../ui/feedback";

export interface ActingBannerProps {
  workspaceName: string;
  workspaceId: string;
}

/** Full-width red band shown when an admin is acting as this workspace. Text is white on red (>4.5:1). */
export function ActingBanner({ workspaceName, workspaceId }: ActingBannerProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);

  const stop = async () => {
    try {
      await adminApi.stopActingAs();
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspaceId] });
      setError(null);
      await navigate({ to: "/admin" });
    } catch (failure: unknown) {
      setError(failure);
    }
  };

  return (
    <div role="status" className="w-full bg-red text-white">
      <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2 md:px-6">
        <span className="font-display text-lg leading-tight font-bold tracking-[0.01em] uppercase">
          {t("workspace.acting", { name: workspaceName })}
        </span>
        <button
          type="button"
          onClick={() => void stop()}
          className="inline-flex min-h-11 items-center justify-center rounded-control bg-board px-4 font-semibold text-red transition-colors duration-[120ms] hover:bg-red-wash"
        >
          {t("workspace.stopActing")}
        </button>
      </div>
      {error ? (
        <div className="mx-auto w-full max-w-5xl px-4 py-2 md:px-6">
          <ErrorText error={error} />
        </div>
      ) : null}
    </div>
  );
}
